import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { cn } from '../lib/utils';
import { RUBRICAS_OFICIALES, RUBRICAS_POR_TAREA } from '../lib/rubricas-data';
import { RUBRICAS_OFICIALES_M2, RUBRICAS_M2_POR_TAREA } from '../lib/rubricas-modulo2-data';
import { RUBRICAS_OFICIALES_M3, RUBRICAS_M3_POR_TAREA, ESTRATEGIA_MODULO_3 } from '../lib/rubricas-modulo3-data';
import IndicadorImpactBanner from '../components/IndicadorImpactBanner';
import RubricasParvularia from './RubricasParvularia';
import { isParvularia } from '../lib/parvularia-data';
import { useIsParvularia } from '../context/TesterModalityContext';
import { useAuth } from '../context/AuthContext';
import {
    BookOpen, ChevronRight, ChevronDown, ArrowLeft,
    Target, Info, FileText, Award, BarChart3, TrendingUp,
    Heart, Users, MessageCircle, Sparkles, Compass, AlertCircle, Star
} from 'lucide-react';

const NIVEL_STYLES = {
    insatisfactorio: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-400' },
    basico: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
    competente: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
    destacado: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800', badge: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-400' },
};

const TAREA_META = {
    'tarea-1': { label: 'Tarea 1', titulo: 'Planificación de la enseñanza', color: 'sky' },
    'tarea-2': { label: 'Tarea 2', titulo: 'Evaluación formativa', color: 'amber' },
    'tarea-3': { label: 'Tarea 3', titulo: 'Reflexión Pedagógica', color: 'violet' },
    'tarea-4': { label: 'Tarea 4', titulo: 'Ambiente y participación en el aula', color: 'sky' },
    'tarea-5': { label: 'Tarea 5', titulo: 'Enseñanza y aprendizaje profundo', color: 'amber' },
    'tarea-6': { label: 'Tarea 6', titulo: 'Experiencias, retroalimentación y equidad', color: 'violet' },
    // Módulo 3 — Trabajo Colaborativo para el Desarrollo Profesional (rúbricas 2026)
    'tarea-7': { label: 'Tarea 5 · A.1', titulo: 'Relevancia del problema a partir del análisis de evidencia', color: 'rose' },
    'tarea-8': { label: 'Tarea 5 · A.2', titulo: 'Diálogo pedagógico en torno a la implementación', color: 'teal' },
    'tarea-9': { label: 'Tarea 5 · B/C', titulo: 'Evaluación conjunta y aprendizajes profesionales', color: 'amberWarm' },
};

const MODULO_META = {
    'modulo-1': {
        id: 'modulo-1',
        label: 'Módulo 1',
        titulo: 'Planificación, evaluación y reflexión',
        rubricas: RUBRICAS_OFICIALES,
        porTarea: RUBRICAS_POR_TAREA,
        tareasOrden: ['tarea-1', 'tarea-2', 'tarea-3'],
        tema: 'tech',
    },
    'modulo-2': {
        id: 'modulo-2',
        label: 'Módulo 2',
        titulo: 'Clase grabada',
        rubricas: RUBRICAS_OFICIALES_M2,
        porTarea: RUBRICAS_M2_POR_TAREA,
        tareasOrden: ['tarea-4', 'tarea-5', 'tarea-6'],
        tema: 'tech',
    },
    'modulo-3': {
        id: 'modulo-3',
        label: 'Módulo 3',
        titulo: 'Trabajo colaborativo para el desarrollo profesional',
        subtitulo: 'Tarea 5 — Rúbricas oficiales 2026 (4 indicadores vigentes)',
        rubricas: RUBRICAS_OFICIALES_M3,
        porTarea: RUBRICAS_M3_POR_TAREA,
        tareasOrden: ['tarea-7', 'tarea-8', 'tarea-9'],
        tema: 'reflexivo',
    },
};

const ALL_RUBRICAS = [...RUBRICAS_OFICIALES, ...RUBRICAS_OFICIALES_M2, ...RUBRICAS_OFICIALES_M3];

// Mapeo de Nivel de Dificultad Nacional → estilo + explicación estratégica
const DIFICULTAD_META = {
    'Baja': {
        color: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        dot: 'bg-emerald-500',
        explicacion: 'Indicador con buen desempeño nacional. La mayoría de los/as docentes alcanza Competente o Destacado. Mantén el estándar sin sobreinvertir.',
    },
    'Media': {
        color: 'bg-amber-50 text-amber-800 border-amber-200',
        dot: 'bg-amber-500',
        explicacion: 'Indicador con desempeño nacional mixto. Un esfuerzo bien dirigido puede llevarte de Básico a Competente.',
    },
    'Alta': {
        color: 'bg-orange-50 text-orange-800 border-orange-200',
        dot: 'bg-orange-500',
        explicacion: 'Indicador exigente: suele presentar mayores dificultades por el nivel de reflexión pedagógica y profundidad argumentativa requerida.',
    },
    'Muy Alta': {
        color: 'bg-rose-50 text-rose-800 border-rose-200',
        dot: 'bg-rose-500',
        explicacion: 'Indicador altamente crítico: requiere comprensión estratégica del sistema, evidencia rigurosa y autenticidad profesional.',
    },
};

const TIPO_META = {
    'Obligatorio': {
        label: 'Obligatorio',
        className: 'bg-indigo-100 text-indigo-900 border border-indigo-200',
        icon: AlertCircle,
    },
    'Voluntario': {
        label: 'Voluntario',
        className: 'bg-yellow-50 text-yellow-900 border border-yellow-200',
        icon: Star,
    },
};

function NivelCard({ nivelKey, nivel, porcentaje }) {
    const style = NIVEL_STYLES[nivelKey];
    return (
        <div className={cn('rounded-lg border p-4', style.border, style.bg)} data-testid={`nivel-${nivelKey}`}>
            <div className="flex items-center justify-between mb-2">
                <Badge className={cn('text-xs font-semibold', style.badge)}>{nivel.label}</Badge>
                {porcentaje != null && (
                    <span className={cn('text-lg font-bold font-mono', style.text)} data-testid={`pct-${nivelKey}`}>
                        {porcentaje}%
                    </span>
                )}
            </div>
            <ul className="space-y-1.5">
                {nivel.criterios.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                        <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} />
                        <span className={style.text}>{c}</span>
                    </li>
                ))}
            </ul>
            {porcentaje != null && (
                <p className={cn('mt-3 text-xs', style.text, 'opacity-70')}>
                    {porcentaje}% de los docentes a nivel nacional
                </p>
            )}
        </div>
    );
}

const DIFICULTAD_STYLES = {
    'Alta': { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', badge: 'bg-rose-600 text-white' },
    'Media': { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', badge: 'bg-amber-600 text-white' },
    'Baja': { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', badge: 'bg-emerald-600 text-white' },
};

function ResultadosNacionalesSection({ resultados }) {
    if (!resultados) return null;
    const dStyle = DIFICULTAD_STYLES[resultados.dificultad] || DIFICULTAD_STYLES['Media'];

    return (
        <div className="space-y-4" data-testid="resultados-nacionales-section">
            {/* Header */}
            <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-slate-600" />
                <h3 className="font-heading font-semibold text-slate-900">Resultados Nacionales del Portafolio Docente</h3>
            </div>

            {/* Percentage bars */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Distribución nacional ({resultados.totalDocentes?.toLocaleString('es-CL')} docentes evaluados)
                </p>
                {[
                    { key: 'destacado', label: 'Destacado', color: 'bg-indigo-400' },
                    { key: 'competente', label: 'Competente', color: 'bg-emerald-400' },
                    { key: 'basico', label: 'Básico', color: 'bg-amber-400' },
                    { key: 'insatisfactorio', label: 'Insatisfactorio', color: 'bg-rose-400' },
                ].map(({ key, label, color }) => (
                    <div key={key} className="flex items-center gap-3">
                        <span className="w-28 text-xs text-slate-600 font-medium">{label}</span>
                        <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                                className={cn('h-full rounded-full transition-all', color)}
                                style={{ width: `${Math.max(resultados[key], 2)}%` }}
                            />
                        </div>
                        <span className="w-14 text-right text-sm font-mono font-bold text-slate-700">
                            {resultados[key]}%
                        </span>
                    </div>
                ))}
            </div>

            {/* Lectura del sistema */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-5 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Lectura del sistema</p>
                <p className="text-sm text-slate-700 leading-relaxed">{resultados.lecturaSistema}</p>
            </div>

            {/* Dificultad */}
            <div className={cn('rounded-xl border-2 p-5 space-y-2', dStyle.border, dStyle.bg)}>
                <div className="flex items-center gap-3">
                    <Badge className={cn('text-xs font-bold px-3 py-1', dStyle.badge)}>
                        Dificultad {resultados.dificultad}
                    </Badge>
                    <span className={cn('text-xs', dStyle.text)}>
                        {resultados.dificultad === 'Alta' && 'Alta presencia de Básico/Insuficiente, bajo % Destacado'}
                        {resultados.dificultad === 'Media' && 'Mayoría en Competente, margen para alcanzar Destacado'}
                        {resultados.dificultad === 'Baja' && 'Alto porcentaje en Destacado, fortaleza del sistema'}
                    </span>
                </div>
            </div>

            {/* Implicancia */}
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-5">
                <div className="flex items-start gap-2">
                    <TrendingUp className="mt-0.5 h-4 w-4 text-blue-600 shrink-0" />
                    <div>
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Implicancia para tu portafolio</p>
                        <p className="mt-1 text-sm text-blue-800 leading-relaxed">{resultados.implicancia}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RubricaDetail({ rubrica, navigate }) {
    const [showNotas, setShowNotas] = useState(false);
    const tMeta = TAREA_META[rubrica.tarea];

    return (
        <Layout>
            <div className="mx-auto max-w-3xl space-y-6 animate-fade-in">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <button onClick={() => navigate('/rubricas')} className="flex items-center gap-1 hover:text-slate-900 transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                        Rúbricas
                    </button>
                    <ChevronRight className="h-4 w-4" />
                    <span className="text-slate-400">{tMeta.label}</span>
                    <ChevronRight className="h-4 w-4" />
                    <span className="text-slate-900">Indicador {rubrica.indicadorNum}</span>
                </div>

                {/* Header */}
                <div className="rounded-xl border-2 border-slate-200 bg-white p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                            <Award className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">{tMeta.label}</Badge>
                                <Badge variant="outline" className="text-xs">Indicador {rubrica.indicadorNum}</Badge>
                                {rubrica.tipo && (() => {
                                    const tipoStyle = TIPO_META[rubrica.tipo];
                                    const Icon = tipoStyle.icon;
                                    return (
                                        <span
                                            className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide', tipoStyle.className)}
                                            data-testid={`detail-tipo-${rubrica.tipo.toLowerCase()}`}
                                        >
                                            <Icon className="h-3 w-3" />
                                            {tipoStyle.label}
                                        </span>
                                    );
                                })()}
                            </div>
                            <h1 className="mt-2 font-heading text-xl font-bold text-slate-900 leading-snug sm:text-2xl">
                                {rubrica.nombre}
                            </h1>
                            <p className="mt-2 text-sm text-slate-500 leading-relaxed whitespace-pre-line">
                                {rubrica.descripcion}
                            </p>
                            {rubrica.dificultad && (() => {
                                const d = DIFICULTAD_META[rubrica.dificultad];
                                return (
                                    <div className={cn('mt-4 flex items-start gap-3 rounded-lg border px-3 py-2.5', d.color)}>
                                        <Compass className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
                                        <div className="text-xs leading-relaxed">
                                            <span className="font-bold uppercase tracking-wide">Dificultad nacional: {rubrica.dificultad}</span>
                                            <p className="mt-1 opacity-90">{d.explicacion}</p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* Competente vs Destacado (Módulo 3) */}
                {rubrica.competenteVsDestacado && (
                    <CompetenteVsDestacado data={rubrica.competenteVsDestacado} />
                )}

                {/* Mini-banner de impacto estratégico (capa motor) */}
                <IndicadorImpactBanner indicadorId={rubrica.indicadorId} />

                {/* Evidencia */}
                <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                    <div className="flex items-start gap-2">
                        <FileText className="mt-0.5 h-4 w-4 text-blue-600 shrink-0" />
                        <div>
                            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Evidencia a revisar</p>
                            <p className="mt-1 text-sm text-blue-800 leading-relaxed">{rubrica.evidencia}</p>
                        </div>
                    </div>
                </div>

                {/* Performance Levels */}
                <div className="space-y-3">
                    <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Niveles de Desempeño
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <NivelCard nivelKey="destacado" nivel={rubrica.niveles.destacado} porcentaje={rubrica.resultadosNacionales?.destacado} />
                        <NivelCard nivelKey="competente" nivel={rubrica.niveles.competente} porcentaje={rubrica.resultadosNacionales?.competente} />
                        <NivelCard nivelKey="basico" nivel={rubrica.niveles.basico} porcentaje={rubrica.resultadosNacionales?.basico} />
                        <NivelCard nivelKey="insatisfactorio" nivel={rubrica.niveles.insatisfactorio} porcentaje={rubrica.resultadosNacionales?.insatisfactorio} />
                    </div>
                </div>

                {/* Resultados Nacionales Section */}
                <ResultadosNacionalesSection resultados={rubrica.resultadosNacionales} />

                {/* Notas */}
                {rubrica.notas.length > 0 && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50/50">
                        <button
                            className="flex w-full items-center justify-between p-4"
                            onClick={() => setShowNotas(!showNotas)}
                            data-testid="toggle-notas"
                        >
                            <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                <Info className="h-4 w-4" />
                                Notas y aclaraciones ({rubrica.notas.length})
                            </span>
                            {showNotas ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        </button>
                        {showNotas && (
                            <div className="border-t border-slate-200 px-4 pb-4 pt-3">
                                <ul className="space-y-2">
                                    {rubrica.notas.map((nota, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
                                            {nota}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => navigate(`/chat?tarea=${rubrica.tarea}&indicador=${rubrica.indicadorId}`)}
                        data-testid="rubrica-consultar-ia"
                    >
                        Consultar IA sobre este indicador
                    </Button>
                    <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => navigate(`/borradores?tarea=${rubrica.tarea}&indicador=${rubrica.indicadorId}`)}
                        data-testid="rubrica-borrador"
                    >
                        Crear borrador
                    </Button>
                </div>
            </div>
        </Layout>
    );
}

function RubricaListItem({ rubrica, navigate }) {
    const style = TAREA_META[rubrica.tarea];
    const tipoStyle = rubrica.tipo ? TIPO_META[rubrica.tipo] : null;
    const dificultadStyle = rubrica.dificultad ? DIFICULTAD_META[rubrica.dificultad] : null;
    return (
        <div
            className="group flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm cursor-pointer"
            onClick={() => navigate(`/rubricas/${rubrica.id}`)}
            data-testid={`rubrica-item-${rubrica.id}`}
        >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 text-sm font-bold">
                {rubrica.indicadorNum}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-medium text-slate-900 leading-snug group-hover:text-slate-700">{rubrica.nombre}</h4>
                    {tipoStyle && (
                        <span
                            className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', tipoStyle.className)}
                            data-testid={`rubrica-tipo-${rubrica.tipo.toLowerCase()}`}
                        >
                            <tipoStyle.icon className="h-3 w-3" />
                            {tipoStyle.label}
                        </span>
                    )}
                </div>
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{rubrica.descripcion.split('\n')[0]}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <div className="flex gap-1.5">
                        {Object.keys(NIVEL_STYLES).reverse().map((key) => (
                            <span key={key} className={cn('h-1.5 w-6 rounded-full', NIVEL_STYLES[key].dot, 'opacity-60')} />
                        ))}
                    </div>
                    {dificultadStyle && (
                        <span
                            className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold', dificultadStyle.color)}
                            title={dificultadStyle.explicacion}
                        >
                            <span className={cn('h-1.5 w-1.5 rounded-full', dificultadStyle.dot)} />
                            Dificultad {rubrica.dificultad}
                        </span>
                    )}
                </div>
            </div>
            <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-300 group-hover:text-slate-500" />
        </div>
    );
}

function TareaSection({ tareaId, rubricasList, navigate }) {
    const [expanded, setExpanded] = useState(true);
    const meta = TAREA_META[tareaId];
    const colorMap = {
        sky: 'border-sky-200 bg-sky-50/40',
        amber: 'border-amber-200 bg-amber-50/40',
        violet: 'border-violet-200 bg-violet-50/40',
        rose: 'border-rose-200 bg-rose-50/50',
        teal: 'border-teal-200 bg-teal-50/50',
        amberWarm: 'border-amber-300 bg-amber-50/60',
    };
    const badgeMap = {
        sky: 'bg-sky-100 text-sky-800',
        amber: 'bg-amber-100 text-amber-800',
        violet: 'bg-violet-100 text-violet-800',
        rose: 'bg-rose-100 text-rose-800',
        teal: 'bg-teal-100 text-teal-800',
        amberWarm: 'bg-amber-100 text-amber-900',
    };

    return (
        <div className="space-y-3" data-testid={`rubrica-tarea-section-${tareaId}`}>
            <button
                className={cn('w-full flex items-center justify-between rounded-xl border-2 p-4 transition-colors', colorMap[meta.color])}
                onClick={() => setExpanded(!expanded)}
                data-testid={`rubrica-tarea-toggle-${tareaId}`}
            >
                <div className="flex items-center gap-3">
                    <Badge className={cn('text-xs font-semibold', badgeMap[meta.color])}>{meta.label}</Badge>
                    <span className="font-heading font-semibold text-slate-900 text-sm sm:text-base">{meta.titulo}</span>
                    <span className="text-xs text-slate-400">{rubricasList.length} indicador{rubricasList.length > 1 ? 'es' : ''}</span>
                </div>
                {expanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
            </button>
            {expanded && (
                <div className="space-y-2 pl-1">
                    {rubricasList.map((r) => (
                        <RubricaListItem key={r.id} rubrica={r} navigate={navigate} />
                    ))}
                </div>
            )}
        </div>
    );
}

function CompetenteVsDestacado({ data }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50/70 via-white to-amber-50/40 overflow-hidden" data-testid="competente-vs-destacado">
            <button
                onClick={() => setOpen(!open)}
                className="flex w-full items-center justify-between p-5 text-left"
                data-testid="toggle-competente-vs-destacado"
            >
                <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
                        <Sparkles className="h-5 w-5" />
                    </span>
                    <span>
                        <span className="block font-heading font-bold text-slate-900">¿Qué diferencia un Competente de un Destacado?</span>
                        <span className="block text-xs text-slate-500 mt-0.5">Resumen estratégico basado en la rúbrica oficial</span>
                    </span>
                </span>
                {open ? <ChevronDown className="h-5 w-5 text-rose-400" /> : <ChevronRight className="h-5 w-5 text-rose-400" />}
            </button>
            {open && (
                <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2">
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Competente</p>
                        <p className="mt-2 text-sm text-slate-800 leading-relaxed">{data.competente}</p>
                    </div>
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Destacado</p>
                        <p className="mt-2 text-sm text-slate-800 leading-relaxed">{data.destacado}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

function ModuloHeroReflexivo({ moduloActivo }) {
    return (
        <div className="rounded-2xl border border-rose-200/70 bg-gradient-to-br from-rose-50/70 via-amber-50/40 to-teal-50/40 p-6 sm:p-7" data-testid="modulo-3-hero">
            <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/80 ring-1 ring-rose-200 text-rose-700 shadow-sm">
                    <Heart className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <Badge className="bg-rose-100 text-rose-900 text-xs border border-rose-200">{moduloActivo.label}</Badge>
                    <h2 className="mt-2 font-heading text-lg font-bold text-slate-900 leading-snug">{moduloActivo.titulo}</h2>
                    {moduloActivo.subtitulo && (
                        <p className="mt-1 text-sm text-slate-600 italic leading-relaxed">{moduloActivo.subtitulo}</p>
                    )}
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className="flex items-start gap-2.5">
                            <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                            <p className="text-xs text-slate-700 leading-relaxed">
                                Reflexionar pedagógicamente <strong>junto a otros/as</strong>
                            </p>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Users className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                            <p className="text-xs text-slate-700 leading-relaxed">
                                Construir <strong>conocimiento docente</strong> en comunidad
                            </p>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Compass className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                            <p className="text-xs text-slate-700 leading-relaxed">
                                Cuestionar <strong>creencias</strong> y crecer profesionalmente
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function EstrategiaModulo3Card() {
    return (
        <div className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50/80 to-rose-50/40 p-6" data-testid="estrategia-modulo-3">
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                    <Compass className="h-5 w-5" />
                </div>
                <div>
                    <h3 className="font-heading font-bold text-slate-900 leading-snug">{ESTRATEGIA_MODULO_3.titulo}</h3>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{ESTRATEGIA_MODULO_3.cuerpo}</p>
                </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {ESTRATEGIA_MODULO_3.pilares.map((p, i) => (
                    <div key={i} className="rounded-xl bg-white/70 border border-amber-100/80 p-4">
                        <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">{p.titulo}</p>
                        <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{p.texto}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Rubricas() {
    const { profile } = useAuth();
    const { rubricId } = useParams();
    const navigate = useNavigate();
    const [moduloSeleccionado, setModuloSeleccionado] = useState('modulo-1');
    const esParvularia = useIsParvularia(profile?.perfil);

    // Fase A · Parvularia (+ tester QA): dispatcher.
    if (esParvularia) {
        return <RubricasParvularia />;
    }

    // Detail view
    if (rubricId) {
        const rubrica = ALL_RUBRICAS.find((r) => r.id === rubricId);
        if (rubrica) {
            return <RubricaDetail rubrica={rubrica} navigate={navigate} />;
        }
        return (
            <Layout>
                <div className="flex h-96 flex-col items-center justify-center gap-4">
                    <p className="text-slate-500">Rúbrica no encontrada</p>
                    <Button variant="outline" onClick={() => navigate('/rubricas')}>Volver</Button>
                </div>
            </Layout>
        );
    }

    const moduloActivo = MODULO_META[moduloSeleccionado];

    // List view
    return (
        <Layout>
            <div className="mx-auto max-w-3xl space-y-6 animate-fade-in">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900 md:text-3xl">
                        Rúbricas del Portafolio
                    </h1>
                    <p className="mt-1 text-slate-500">
                        Criterios oficiales de evaluación CPEIP/MINEDUC por módulo
                    </p>
                </div>

                {/* Module selector */}
                <div className="flex flex-wrap gap-2" data-testid="modulo-selector">
                    {Object.values(MODULO_META).map((m) => {
                        const isM3 = m.id === 'modulo-3';
                        const active = moduloSeleccionado === m.id;
                        return (
                            <button
                                key={m.id}
                                onClick={() => setModuloSeleccionado(m.id)}
                                className={cn(
                                    'flex items-center gap-2 rounded-xl border-2 px-4 py-2 text-sm font-medium transition-all',
                                    active
                                        ? isM3
                                            ? 'border-rose-500 bg-gradient-to-r from-rose-500 to-amber-500 text-white shadow-sm'
                                            : 'border-slate-900 bg-slate-900 text-white shadow-sm'
                                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                )}
                                data-testid={`modulo-tab-${m.id}`}
                            >
                                {isM3 ? <Heart className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
                                {m.label}
                            </button>
                        );
                    })}
                </div>

                {/* Module header — versión cálida para Módulo 3 */}
                {moduloActivo.tema === 'reflexivo' ? (
                    <ModuloHeroReflexivo moduloActivo={moduloActivo} />
                ) : (
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                            <BookOpen className="h-5 w-5" />
                        </div>
                        <div>
                            <Badge className="bg-slate-900 text-white text-xs">{moduloActivo.label}</Badge>
                            <h2 className="mt-0.5 font-heading text-base font-bold text-slate-900">{moduloActivo.titulo}</h2>
                        </div>
                    </div>
                )}

                {/* Tasks with rubrics */}
                {moduloActivo.tareasOrden.map((tareaId) => {
                    const list = moduloActivo.porTarea[tareaId] || [];
                    if (list.length === 0) return null;
                    return <TareaSection key={tareaId} tareaId={tareaId} rubricasList={list} navigate={navigate} />;
                })}

                {/* Mensaje estratégico exclusivo del Módulo 3 */}
                {moduloActivo.tema === 'reflexivo' && <EstrategiaModulo3Card />}

                {/* How to use */}
                <Card className="border-slate-200 bg-slate-50/50">
                    <CardContent className="p-6">
                        <h3 className="font-heading font-semibold text-slate-900">
                            ¿Cómo usar las rúbricas?
                        </h3>
                        <ul className="mt-3 space-y-2 text-sm text-slate-600">
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Revisa los criterios de cada nivel antes de escribir tu respuesta
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Apunta al nivel Competente como mínimo; alcanzar Destacado es una meta, no una obligación
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Usa "Consultar IA" para recibir orientación específica según la rúbrica
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                En Módulos 2 y 3 la IA adapta su análisis a tu perfil (Generalista / Básica / Media)
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                En Módulo 3 prioriza los indicadores <strong className="text-indigo-700">Obligatorios</strong>; los <strong className="text-yellow-700">Voluntarios</strong> son para Excelencia
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
